import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Home, Users, Video, User, CreditCard, LogOut, Network, Calendar, 
  MessageCircle, Sparkles, Briefcase, Settings, Globe, Image, 
  Presentation, FileText, Mail, BarChart3, CalendarClock, Menu, X,
  Camera, Building2, ChevronDown, ChevronRight, Palette, Zap
} from 'lucide-react';
import { clearAuth, getUser, isAuthenticated } from '../lib/auth';
import NotificationPanel from './NotificationPanel';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuth = isAuthenticated();
  const user = getUser();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    scheduling: true,
    websites: false,
    business: true,
    design: true,
    admin: false
  });

  const handleLogout = () => {
    clearAuth();
    navigate('/');
    window.location.reload();
  };

  if (!isAuth) return null;

  const isActive = (path) => location.pathname === path;
  const isInSection = (paths) => paths.some(path => location.pathname.startsWith(path));

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const menuSections = [
    {
      title: 'Main',
      items: [
        { path: '/', label: 'Dashboard', icon: Home },
        { path: `/profile/${user?.id}`, label: 'My Profile', icon: User },
        { path: '/browse', label: 'Browse Users', icon: Users },
      ]
    },
    {
      title: 'Calendar & Scheduling',
      key: 'scheduling',
      expandable: true,
      items: [
        { path: '/digi-calendar', label: 'DigiCalendar', icon: Calendar },
        { path: '/event-types', label: 'Event Types', icon: CalendarClock },
        { path: '/meetings', label: 'My Meetings', icon: Video },
      ]
    },
    {
      title: 'Communication',
      items: [
        { path: '/digi-connect', label: 'Digi Connect', icon: Network },
        { path: '/digi-meet', label: 'Digi Meet', icon: Video },
        { path: '/community', label: 'Community Hub', icon: MessageCircle },
        { path: '/community-videos', label: 'Community Videos', icon: Video },
        { path: '/chat', label: 'Chat', icon: MessageCircle },
      ]
    },
    {
      title: 'Websites & Content',
      key: 'websites',
      expandable: true,
      items: [
        { path: '/website-builder', label: 'Website Builder', icon: Globe },
        { path: '/auto-website', label: 'Auto Website', icon: Zap },
        { path: '/quick-website', label: 'Quick Website', icon: Sparkles },
        { path: '/digi-website', label: 'Digi Website', icon: Building2 },
      ]
    },
    {
      title: 'Business Tools',
      key: 'business',
      expandable: true,
      items: [
        // { path: '/ai-team', label: 'Digi Squad (AI)', icon: Sparkles },
        { path: '/crm-leads', label: 'CRM & Leads', icon: Users },
        { path: '/contacts', label: 'Contacts', icon: Users },
        { path: '/contact-groups', label: 'Contact Groups', icon: Users },
        // { path: '/hr-ops', label: 'HR Operations', icon: Briefcase },
        // { path: '/digi-strategy', label: 'Digi Strategy', icon: Briefcase },
      ]
    },
    {
      title: 'Design & Media',
      key: 'design',
      expandable: true,
      items: [
        // { path: '/digi-poster', label: 'Poster Creator', icon: Image },
        // { path: '/digi-branding', label: 'Digi Branding', icon: Palette },
        // { path: '/digi-editor', label: 'Digi Editor', icon: Sparkles },
        { path: '/presentor', label: 'Presentor', icon: Presentation },
        // DigiSlides removed
        { path: '/events', label: 'Photo/Video Events', icon: Camera },
        // { path: '/photobooth', label: 'Photobooth', icon: Camera }, // Temporarily disabled
      ]
    },
    {
      title: 'Settings',
      items: [
        { path: '/settings', label: 'Settings', icon: Settings },
        { path: '/subscription', label: 'Subscription', icon: CreditCard },
      ]
    }
  ];

  const SidebarContent = () => (
    <>
      {/* Logo/Brand */}
      <div className="p-4 border-b border-slate-200">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">DD</span>
          </div>
          {!collapsed && (
            <div>
              <h2 className="text-lg font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                DigiDream
              </h2>
              <p className="text-xs text-slate-500">Solutions</p>
            </div>
          )}
        </Link>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-slate-600" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          )}
        </div>
        {!collapsed && (
          <div className="mt-3">
            <NotificationPanel />
          </div>
        )}
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-6">
        {menuSections.map((section, idx) => (
          <div key={idx}>
            <div className="flex items-center justify-between mb-2">
              {!collapsed && (
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {section.title}
                </h3>
              )}
              {section.expandable && !collapsed && (
                <button
                  onClick={() => toggleSection(section.key)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  {expandedSections[section.key] ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
            <div className="space-y-1">
              {(!section.expandable || expandedSections[section.key] || collapsed) && section.items.map((item, itemIdx) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={itemIdx}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                      active
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-slate-700 hover:bg-slate-100'
                    } ${collapsed ? 'justify-center' : ''}`}
                    title={collapsed ? item.label : ''}
                    onClick={() => setMobileOpen(false)}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-blue-700' : 'text-slate-500'}`} />
                    {!collapsed && <span className="text-sm">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer Actions */}
      <div className="p-4 border-t border-slate-200 space-y-2">
        {!collapsed ? (
          <>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setCollapsed(true)}
            >
              <Menu className="w-4 h-4 mr-2" />
              Collapse
            </Button>
          </>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setCollapsed(false)}
          >
            <Menu className="w-4 h-4" />
          </Button>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div 
        className={`hidden md:flex md:flex-col fixed left-0 top-0 h-screen bg-white border-r border-slate-200 transition-all duration-300 z-40 ${
          collapsed ? 'w-20' : 'w-64'
        }`}
      >
        <SidebarContent />
      </div>

      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-4 left-4 z-50">
        <Button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="bg-white border border-slate-200 shadow-lg"
          size="sm"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div 
        className={`md:hidden fixed left-0 top-0 h-screen w-64 bg-white border-r border-slate-200 z-50 transition-transform duration-300 flex flex-col ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </div>

      {/* Spacer for content - adjusts based on sidebar state */}
      <div className={`hidden md:block ${collapsed ? 'w-20' : 'w-64'} flex-shrink-0`} />
    </>
  );
};

export default Sidebar;
